import React, { FC, useEffect } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import classNames from 'classnames';
import { isEmpty } from 'lodash';

import lang from '../../lib/language';
import { RootState } from '../../lib/store';
import { getIndividualUserName } from '../../lib/store/crm';
import { LeaderboardRequestedCompetitor } from '../../lib/store/reporting/types';
import { formatCapitalTournamentPrize, formatNumber } from '../../lib/util/DataHelpers';

import { BaseTable, UserPhoto } from '../../components';
import { useIsMobile } from '../../hooks';

import { DEFAULT_POSITION_DATA, PositionHeaders } from './Leaderboard.constants';
import SkeletonLoaderRow from './SkeletonLoaderRow';

import styles from './Leaderboard.module.scss';

type Props = ReduxProps & {
  data: LeaderboardRequestedCompetitor | undefined;
  isLoading: boolean;
  initialAdjustmentAmount: number;
  isTournamentStarted: boolean;
};

const Position: FC<Props> = (props: Props): JSX.Element => {
  const { data, getUserName, isLoading, extendedInfo, individualUserName,
    initialAdjustmentAmount, isTournamentStarted } = props;
  const isMobile = useIsMobile();
  const tableHeaders = PositionHeaders.map((item: string) => <th className={styles.transparentRow}>{item}</th>);
  const hasDataToDisplay = !isLoading && !isEmpty(data);
  const defaultOrData = hasDataToDisplay ? data : DEFAULT_POSITION_DATA;
  const { pnL, netEquity, prize, rank } = defaultOrData;

  useEffect(() => {
    getUserName();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const getFirstAndLastChar = (): string => {
    const firstName = extendedInfo?.first_name;
    const lastName = extendedInfo?.last_name;

    if (firstName && lastName) {
      return `${firstName[0]}${lastName[0]}`;
    }

    return '-';
  };

  const renderContent = () => {
    const percentProfit = hasDataToDisplay && netEquity
      ? ((netEquity - initialAdjustmentAmount) / initialAdjustmentAmount) * 100
      : 0;
    return (
      <tr className={styles.row}>
        <td className={styles.noBorder}>
          <div className={isMobile ? styles.indexMob : styles.index}>
            {isTournamentStarted
              ? `#${rank}`
              : '-'}
          </div>
        </td>
        <td className={classNames(styles.photo, { [styles.noBorder]: true })}>
          <UserPhoto customText={getFirstAndLastChar()} isHeader />
        </td>
        <td className={classNames(styles.nicknameTd, { [styles.noBorder]: true })}>
          <div className={styles.nickname}>
            {isMobile ? (
              <div className={styles.text}>
                <div>{individualUserName}</div>
                {prize > 0 && isTournamentStarted && (
                <div className={styles.reward}>
                  {lang.commonPrize()}
                  {formatCapitalTournamentPrize(prize, true)}
                </div>
                )}
              </div>
            ) : (
              <span>{individualUserName}</span>
            )}
          </div>
        </td>
        <td
          className={classNames(styles.profit, {
            [styles.noBorder]: true,
            [styles.positive]: pnL > 0,
            [styles.negative]: pnL < 0,
          })}
        >
          <div>{hasDataToDisplay && isTournamentStarted ? `${pnL > 0 ? '+' : ''}$${formatNumber(pnL)}` : '-'}</div>
          {isMobile && <div>{hasDataToDisplay && isTournamentStarted ? `(${percentProfit > 0 ? '+' : ''}${formatNumber(percentProfit)}%)` : '-'}</div>}
        </td>
        {!isMobile && (
          <td
            className={classNames(styles.percentChange, {
              [styles.noBorder]: true,
              [styles.positive]: percentProfit > 0,
              [styles.negative]: percentProfit < 0,
            })}
          >
            {hasDataToDisplay && isTournamentStarted ? `${percentProfit > 0 ? '+' : ''} ${formatNumber(percentProfit)}%` : '-'}
          </td>
        )}
        {!isMobile && (
          <td className={classNames(styles.prize, { [styles.noBorder]: true })}>{prize > 0 && isTournamentStarted ? `BGN ${formatCapitalTournamentPrize(prize)}` : '-'}</td>
        )}
      </tr>
    );
  };

  if (!data) {
    return <></>;
  }

  return (
    <>
      <BaseTable className={styles.table}>
        {!isMobile && (
          <thead>
            <tr>{tableHeaders}</tr>
          </thead>
        )}
        <tbody>
          {isLoading ? (
            <>
              <SkeletonLoaderRow />
            </>
          ) : (
            <>{renderContent()}</>
          )}
        </tbody>
      </BaseTable>
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  extendedInfo: state.crm.individualExtendedInfo,
  individualUserName: state.crm.individualUserName,
  leaderboard: state.reporting.leaderboard,
});

const mapDispatchToProps = (dispatch: any) => ({
  getUserName: (): void => dispatch(getIndividualUserName()),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(Position);
